/**
 * Estoque — tipos e rótulos compartilhados (migration 0080).
 *
 * NENHUM ENUM CRU APARECE NA UI. `RETIRADA_CORTE` é nome de coluna; quem lê a
 * tela vê "Retirada para corte". O teste de contrato falha se um valor novo
 * entrar sem rótulo — é o que evita a tela cuspir CONSTANTE_EM_MAIUSCULA.
 */

export type MaterialKind = 'MALHA' | 'AVIAMENTO' | 'GOLA' | 'PUNHO' | 'RIBANA' | 'LISA' | 'CORTADA';
/** Parte de uma peça cortada: a camisa são 1 frente + 1 costas + 2 mangas (a regata não tem manga). */
export type ParteCortada = 'FRENTE' | 'COSTAS' | 'MANGA';
export type MaterialTracking = 'INDIVIDUAL' | 'PACOTE' | 'AGREGADO';
export type StockUnitStatus = 'EM_ESTOQUE' | 'EM_USO' | 'CONSUMIDA';

export type StockLedgerType =
  | 'ENTRADA_COMPRA'
  | 'ENTRADA_PRODUCAO'
  | 'ENTRADA_DEVOLUCAO'
  | 'SAIDA_PEDIDO'
  | 'RETIRADA_CORTE'
  | 'DEVOLUCAO_SOBRA'
  | 'AJUSTE'
  | 'INVENTARIO'
  | 'ESTORNO'
  | 'TRANSFERENCIA';

export const LABEL_MOVIMENTO: Record<StockLedgerType, string> = {
  ENTRADA_COMPRA: 'Entrada por compra',
  ENTRADA_PRODUCAO: 'Entrada da produção',
  ENTRADA_DEVOLUCAO: 'Entrada por devolução',
  SAIDA_PEDIDO: 'Saída para pedido',
  RETIRADA_CORTE: 'Retirada para corte',
  DEVOLUCAO_SOBRA: 'Devolução de sobra',
  AJUSTE: 'Ajuste de saldo',
  INVENTARIO: 'Inventário',
  ESTORNO: 'Estorno',
  TRANSFERENCIA: 'Mudou de caixa',
};

export const PARTES_CORTADA = ['FRENTE', 'COSTAS', 'MANGA'] as const satisfies readonly ParteCortada[];
export const LABEL_PARTE: Record<ParteCortada, string> = { FRENTE: 'Frente', COSTAS: 'Costas', MANGA: 'Manga' };

export const LABEL_STATUS_UNIT: Record<StockUnitStatus, string> = {
  EM_ESTOQUE: 'Em estoque',
  EM_USO: 'Em produção',
  CONSUMIDA: 'Consumida',
};

export const LABEL_TRACKING: Record<MaterialTracking, string> = {
  INDIVIDUAL: 'Rolo a rolo',
  PACOTE: 'Por pacote',
  AGREGADO: 'Por quantidade',
};

export const LABEL_KIND: Record<MaterialKind, string> = {
  MALHA: 'Malha',
  AVIAMENTO: 'Aviamento',
  GOLA: 'Gola',
  PUNHO: 'Punho',
  RIBANA: 'Ribana',
  LISA: 'Peça lisa',
  CORTADA: 'Peça cortada',
};

/**
 * Gola e punho de polo são categorias próprias (0122), e a ribana da camiseta gola O/V também (0131), mas moram no
 * posto de Aviamentos: o posto é uma permissão, a categoria é o filtro do cadastro. A lista vai ao servidor como
 * `?kind=AVIAMENTO,GOLA,PUNHO,RIBANA`.
 */
export const KINDS_DO_POSTO = {
  MALHA: ['MALHA'],
  AVIAMENTO: ['AVIAMENTO', 'GOLA', 'PUNHO', 'RIBANA'],
  LISA: ['LISA'],
} as const satisfies Record<'MALHA' | 'AVIAMENTO' | 'LISA', readonly MaterialKind[]>;
/** Gola ou punho: tecido PA/POLIÉSTER, cor da tabela de gola, sempre por quantidade em unidades. */
export const ehKindGolaPunho = (kind: MaterialKind): kind is 'GOLA' | 'PUNHO' => kind === 'GOLA' || kind === 'PUNHO';

/**
 * As abas da lista de Movimentos: cada uma é um conjunto de tipos do ledger.
 * Os tipos vão ao servidor (`tipos=`) — a lista é paginada lá. A devolução de
 * sobra fica com a retirada para o corte, que é o fluxo a que ela pertence.
 */
export const GRUPOS_MOVIMENTO = [
  { value: 'TODOS', label: 'Tudo', tipos: [] },
  { value: 'ENTRADAS', label: 'Entradas', tipos: ['ENTRADA_COMPRA', 'ENTRADA_PRODUCAO', 'ENTRADA_DEVOLUCAO'] },
  { value: 'SAIDAS', label: 'Saídas e retiradas', tipos: ['SAIDA_PEDIDO', 'RETIRADA_CORTE', 'DEVOLUCAO_SOBRA'] },
  { value: 'INVENTARIO', label: 'Inventário', tipos: ['INVENTARIO'] },
  { value: 'AJUSTES', label: 'Ajustes', tipos: ['AJUSTE'] },
  { value: 'ESTORNOS', label: 'Estornos', tipos: ['ESTORNO'] },
  { value: 'CAIXAS', label: 'Mudanças de caixa', tipos: ['TRANSFERENCIA'] },
] as const satisfies readonly { value: string; label: string; tipos: readonly StockLedgerType[] }[];

export type GrupoMovimento = (typeof GRUPOS_MOVIMENTO)[number]['value'];

/** Verde entra, âmbar sai, cinza corrige. */
export function varianteMovimento(type: StockLedgerType): 'entrada' | 'saida' | 'correcao' {
  if (type.startsWith('ENTRADA') || type === 'DEVOLUCAO_SOBRA') return 'entrada';
  if (type === 'SAIDA_PEDIDO' || type === 'RETIRADA_CORTE') return 'saida';
  return 'correcao';
}

export interface Material {
  id: string;
  kind: MaterialKind;
  tracking: MaterialTracking;
  unit: 'kg' | 'm' | 'un';
  name: string;
  tecido: string | null;
  cor: string | null;
  modelo: string | null;
  tamanho: string | null;
  /** Só peça cortada (0139): FRENTE, COSTAS ou MANGA. */
  parte?: ParteCortada | null;
  /** Fornecedor do material (só malha, 0125): o mesmo tecido e cor de fornecedores diferentes são materiais diferentes. */
  fornecedor?: string | null;
  estoque_minimo: string | null;
  barcode_enforced: boolean;
  active: boolean;
  /** Peça lisa ligada a um produto/variação do GestãoClick (0087). */
  gc_vinculado?: boolean;
}

export interface SaldoMaterial extends Material {
  saldo: string;
  qtd_rolos: number;
  peso_em_estoque: string;
  consumo_30d: string;
  /** Σ reservas ATIVAS (0085). */
  reservado: string;
  /**
   * saldo − reservado. É ESTE o número que a operação usa para decidir:
   * o saldo inclui o que já está separado para outro pedido, e prometer
   * material reservado é como dois pedidos acabam brigando pelo mesmo rolo.
   */
  disponivel: string;
  /** Fornecedores dos rolos em estoque, em minúscula e separados por " · " (só para a busca de Saldos; null = nenhum). */
  fornecedores_rolos?: string | null;
}

export interface StockUnit {
  id: string;
  barcode: string;
  supplier_barcode: string | null;
  material_id: string;
  peso_inicial: string;
  peso_atual: string;
  status: StockUnitStatus;
  fornecedor_texto: string | null;
  /** Fornecedor ligado à lista do GestãoClick; NULL com texto = "outro", para o gerente ligar. */
  gc_fornecedor_id?: string | null;
  /** Custo de aquisição por unidade (kg), quando informado no recebimento ou corrigido depois. */
  valor_unitario?: string | number | null;
  material_nome?: string;
  unit?: string;
  created_at: string;
}

export type PackageOrigem = 'PRODUCAO' | 'RECEBIMENTO' | 'INVENTARIO' | 'DEVOLUCAO';

export const LABEL_ORIGEM_PACOTE: Record<PackageOrigem, string> = {
  PRODUCAO: 'Produzido aqui',
  RECEBIMENTO: 'Comprado pronto',
  INVENTARIO: 'Contagem do inventário',
  DEVOLUCAO: 'Devolução de venda',
};

/**
 * Pacote de peças lisas (Bloco C, 0087).
 *
 * `qty_atual` vem do Postgres como number (integer), diferente dos pesos, que
 * são `numeric` e chegam como string — misturar os dois no mesmo formatador é
 * o erro que faria "12" virar "12,000 un".
 */
export interface StockPackage {
  id: string;
  barcode: string;
  material_id: string;
  qty_inicial: number;
  qty_atual: number;
  origem: PackageOrigem;
  origem_order_id: string | null;
  origem_numero?: string | null;
  material_nome?: string;
  modelo?: string | null;
  cor?: string | null;
  tamanho?: string | null;
  tecido?: string | null;
  created_at: string;
  consumed_at: string | null;
}

export interface Movimento {
  id: number;
  type: StockLedgerType;
  material_id: string;
  material_nome: string;
  /** Para agrupar a lista de Movimentos pelo tecido (`grupoDoMaterial`). */
  material_tecido?: string | null;
  material_kind?: MaterialKind;
  unit: string;
  qty: string;
  unit_barcode: string | null;
  package_barcode?: string | null;
  /** 1º lançamento do rolo/pacote (o que o criou): não é estornável. */
  criou_objeto?: boolean;
  unit_id?: string | null;
  package_id?: string | null;
  /**
   * O objeto da linha NUNCA foi usado (sem saída, pedido, recebimento,
   * ocorrência): é o que faz a tela OFERECER "Excluir de vez" ao admin
   * (0108). O servidor decide de novo, sob lock.
   */
  objeto_nunca_usado?: boolean;
  /** Já desfeito por um estorno integral (0110): o par pode ser excluído pelo admin. */
  estornado?: boolean;
  /**
   * Estado ATUAL do objeto da linha (não o de quando o movimento ocorreu). É o
   * que a aba Movimentos usa para oferecer "Corrigir" na própria linha: o
   * diálogo de ajuste mostra "o sistema tem X" e o teto que a entrada deu.
   * `material_saldo` só vem para material AGREGADO (sem rolo nem pacote).
   */
  tracking?: 'ROLO' | 'PACOTE' | 'AGREGADO' | string;
  package_qty_atual?: number | null;
  package_qty_inicial?: number | null;
  unit_peso_atual?: string | number | null;
  unit_peso_inicial?: string | number | null;
  unit_status?: string | null;
  material_saldo?: string | number | null;
  order_id: string | null;
  /** Número do pedido causador — o uuid não diz nada a quem está no galpão. */
  order_numero?: string | null;
  justification: string | null;
  operator_nome: string | null;
  occurred_at: string;
  created_at: string;
  estorno_de: number | null;
  estorno_parcial: boolean;
  metadata: Record<string, unknown> | null;
}

/**
 * Tamanhos canônicos de peça lisa, na ordem da prateleira.
 *
 * ⚠️ Espelho de `TAMANHOS_CANONICOS` em `server/src/lib/stock-rules.ts` — o
 * teste `tests/stock-rules.test.ts` falha se as duas listas divergirem. O
 * front não importa do servidor (dívida de `rootDir` no typecheck).
 */
export const TAMANHOS_LISA = ['PP', 'P', 'M', 'G', 'GG', 'XGG', 'XXGG', 'XXXGG', 'G4', 'ESPECIAL', 'ÚNICO'] as const;

/**
 * A GRADE PADRÃO — os nove rótulos que saem na linha, sempre, nesta ordem.
 *
 * ⚠️ Não é a mesma lista de `TAMANHOS_LISA`. Aquela é o que o sistema
 * RECONHECE ao ler (inclui G4, ÚNICO e tamanho infantil numérico); esta é o
 * que ele ESCREVE e oferece. Separar as duas é o que permite tirar G4 do
 * formulário sem tornar ilegível o dado antigo que já o tenha.
 */
export const GRADE_PADRAO = ['PP', 'P', 'M', 'G', 'GG', 'XGG', 'XXGG', 'XXXGG', 'ESPECIAL'] as const;

/** Fora da grade padrão, só ÚNICO é oferecido no formulário (G4 saiu — 2026-09-17). */
export const TAMANHOS_OFERECIDOS = [...GRADE_PADRAO, 'ÚNICO'] as const;

/**
 * A GRADE INFANTIL — os seis tamanhos que saem na linha infantil, sempre,
 * nesta ordem (decisão do dono, 2026-09-22: "sempre os seis", como a adulta
 * traz os nove). As CHAVES do mapa continuam sendo o número seco ("2"…"12"):
 * é assim que `materials.tamanho` grava o SKU infantil e que `ALIASES_TAMANHO`
 * reduz "2 ANOS". Só a LINHA escreve "2 ANOS".
 */
export const GRADE_INFANTIL = ['2', '4', '6', '8', '10', '12'] as const;

/**
 * O que o formulário OFERECE no grid infantil: os seis da linha + o 14 anos
 * (pedido do dono, 2026-10-01). O 14 NÃO entra nos "sempre seis": só sai na
 * linha quando tem quantidade, depois de "12 ANOS" — é como o formatador já
 * tratava todo numérico fora dos seis, e o script da arte-final continua
 * lendo a mesma linha de seis rótulos em todo pedido que não tem 14.
 */
export const TAMANHOS_INFANTIS_OFERECIDOS = [...GRADE_INFANTIL, '14'] as const;

/**
 * Mapa → a linha de grade que a fábrica lê e que os scripts do Illustrator do
 * dono consomem: `PP 1/ P / M 1/ G / GG / XGG / XXGG / XXXGG / ESPECIAL`.
 *
 * ⚠️ REPARE NO ESPAÇO: a quantidade COME o espaço antes da barra (`PP 1/`),
 * enquanto o rótulo vazio o mantém (`P / `). É assim porque o formato nasceu
 * do vendedor digitando o número na frente da barra de um template fixo, e é
 * assim que os scripts de fora esperam. `join(' / ')` uniforme produziria
 * `PP 1 / P`, que não é o mesmo texto.
 *
 * Antes daqui saía `P-10, M-20` — só os tamanhos preenchidos. Era um segundo
 * formato, gravado item a item conforme cada um fosse editado com o módulo de
 * estoque ligado, e quebrava os scripts de fora sem avisar ninguém
 * (`docs/REGRAS-DE-NEGOCIO.md` §13.33).
 *
 * `null`/`undefined` devolve vazio (item sem grade); `{}` devolve a linha
 * inteira sem números, que é exatamente o template — por isso
 * `GRADE_TEMPLATE` é derivado daqui e não existe como literal solto.
 */
export function gradeParaTexto(grade: Record<string, number> | null | undefined): string {
  if (!grade) return '';
  const qtd = new Map<string, number>();
  const extras: string[] = [];
  for (const [t, v] of Object.entries(grade)) {
    const n = Math.trunc(Number(v));
    if (!Number.isFinite(n) || n <= 0) continue;
    const canonico = rotuloCanonico(t);
    // ⚠️ Chave em BRANCO não é tamanho e não vira segmento. A importação de
    // lisas usa `grade['']` para dizer "item sem grade, confira" — formatada,
    // ela virava um décimo segmento (`… / ESPECIAL /  50`) numa linha que os
    // scripts de fora leem por posição.
    if (!canonico) continue;
    qtd.set(canonico, (qtd.get(canonico) ?? 0) + n);
    // Tamanho fora dos nove (ÚNICO, G4 legado, infantil numérico) sai DEPOIS
    // de ESPECIAL em vez de ser descartado: perder a quantidade em silêncio
    // seria pior do que uma linha fora do padrão.
    if (!(GRADE_PADRAO as readonly string[]).includes(canonico) && !extras.includes(canonico)) {
      extras.push(canonico);
    }
  }
  if (!qtd.size) return GRADE_PADRAO.map((t) => `${t} `).join('/ ').trimEnd();

  // ⚠️ A PEÇA QUE NÃO USA A GRADE DA FÁBRICA SAI SOZINHA. Nasceu da decisão do
  // dono sobre tamanho único ("ÚNICO não tem o restante da grade"), e vale
  // igual para a grade infantil: pendurar os nove rótulos adultos vazios na
  // frente de `2 5/ 4 5/ 6 5` daria uma linha de doze segmentos para um script
  // que espera nove. Quando NENHUM dos nove foi preenchido, só os próprios
  // tamanhos do item aparecem.
  const usaGradePadrao = GRADE_PADRAO.some((t) => qtd.get(t));
  // ⚠️ A GRADE INFANTIL É OUTRA LINHA, DE OUTRO SCRIPT. Quando nenhum dos nove
  // adultos está preenchido e todo tamanho é numérico, sai a linha que o
  // script da arte-final lê — `2 ANOS 1 / 4 ANOS / 6 ANOS 2 / … / 12 ANOS` —
  // com os seis rótulos sempre presentes e o separador ` / ` uniforme (aqui a
  // quantidade NÃO come o espaço: é o formato que o dono passou, não o adulto).
  // Tamanho numérico fora dos seis ("14") vai depois de "12 ANOS", em ordem.
  if (!usaGradePadrao && extras.every((t) => /^\d{1,2}$/.test(t))) {
    const fora = extras
      .filter((t) => !(GRADE_INFANTIL as readonly string[]).includes(t))
      .sort((a, b) => Number(a) - Number(b));
    return [...GRADE_INFANTIL, ...fora]
      .map((t) => (qtd.get(t) ? `${t} ANOS ${qtd.get(t)}` : `${t} ANOS`))
      .join(' / ');
  }
  const rotulos = usaGradePadrao ? [...GRADE_PADRAO, ...extras] : extras;
  return rotulos
    .map((t) => (qtd.get(t) ? `${t} ${qtd.get(t)}` : `${t} `))
    .join('/ ')
    .trimEnd();
}

/**
 * O rótulo como ele é gravado. Espelho do `normalizarTamanho` do servidor,
 * INCLUSIVE no caso desconhecido: ele tira o prefixo "TAM"/"TAMANHO" antes de
 * desistir, e o front não tirava — `{'TAM ROSA': 7}` saía `TAM ROSA 7` de um
 * lado e `ROSA 7` do outro. Devolve '' para o que não tem rótulo nenhum.
 */
function rotuloCanonico(valor: unknown): string {
  const conhecido = tamanhoCanonicoLisa(String(valor ?? ''));
  if (conhecido) return conhecido;
  return String(valor ?? '').trim().replace(/^tam(anho)?[\s.:-]*/i, '').toUpperCase();
}


/**
 * Tamanho canônico — espelho da tabela `TAMANHOS` de `normalizarTamanho` no
 * servidor (`stock-rules.ts`): "Pequeno", "TAM P", "xl", "G3" viram o valor da
 * prateleira. `tests/stock-rules.test.ts` compara os dois lados. Devolve null
 * para o que não é tamanho conhecido (a lista canônica ou numérico infantil).
 */
export const ALIASES_TAMANHO: Array<[RegExp, string]> = [
  [/^(pp|extra\s*pequen[oa]|xs)$/i, 'PP'],
  [/^(p|pequen[oa]|small|s)$/i, 'P'],
  [/^(m|m[ée]di[oa]|medium)$/i, 'M'],
  [/^(g|grande|large|l)$/i, 'G'],
  [/^(gg|extra\s*grande|xl|xg)$/i, 'GG'],
  [/^(xgg|g1|xxl|exg)$/i, 'XGG'],
  [/^(xxgg|g2|xxxl|3xl)$/i, 'XXGG'],
  [/^(xxxgg|g3|4xl)$/i, 'XXXGG'],
  [/^(g4|5xl)$/i, 'G4'],
  [/^(especial|esp)$/i, 'ESPECIAL'],
  [/^([úu]nico|u|tam[\s.]*[úu]nico)$/i, 'ÚNICO'],
  // Infantil por extenso: "2 ANOS" → "2". Ver o espelho no servidor.
  [/^(\d{1,2})\s*anos?$/i, '$1'],
];

export function tamanhoCanonicoLisa(valor: string | null | undefined): string | null {
  const cru = String(valor ?? '').trim().replace(/^tam(anho)?[\s.:-]*/i, '');
  if (!cru) return null;
  for (const [re, canonico] of ALIASES_TAMANHO) {
    // `$1` existe só no infantil ("8 ANOS" → "8"); os demais são literais.
    if (re.test(cru)) return canonico.includes('$1') ? cru.replace(re, canonico) : canonico;
  }
  return /^\d{1,2}$/.test(cru) ? cru : null;
}

/**
 * A linha sem números — o que o formulário mostra antes de alguém preencher.
 *
 * ⚠️ DERIVADO, nunca um literal. O texto vivia copiado em dois componentes, e
 * o `handleSave` comparava com um deles para não gravar o template como grade:
 * bastava um dos dois mudar para a comparação parar de casar em silêncio.
 * Fica DEPOIS de `tamanhoCanonicoLisa` porque roda na carga do módulo.
 */
export const GRADE_TEMPLATE = gradeParaTexto({});

/**
 * Texto da grade → mapa. Espelho de `parsearGradeTexto` do servidor — MESMA
 * regex e mesma tabela de tamanhos: o front lia "G 1" como ilegível enquanto o
 * servidor reservava por ela, e o bloco Materiais dizia "sem grade" para um
 * pedido que a programação tinha reservado.
 *
 * ⚠️ RÓTULO SEM NÚMERO É ZERO, não lixo. A linha da fábrica traz os nove
 * rótulos sempre, e só alguns com quantidade (`PP 1/ P / M / …`). Exigindo
 * número em TODO token, o parser declarava a linha inteira ilegível: o grid de
 * tamanhos abria vazio sobre um item que tinha grade, o vendedor redigitava e
 * o salvamento apagava o texto original. Era esse o caminho pelo qual o
 * formato antigo se perdia (`docs/REGRAS-DE-NEGOCIO.md` §13.33).
 *
 * Continua devolvendo `null` para o que não dá para ler — token que não é
 * tamanho conhecido — e para a linha sem NENHUMA quantidade (o template puro
 * não é uma grade; zeros não entram no mapa).
 */
export function gradeDeTexto(texto: string | null | undefined): Record<string, number> | null {
  const s = String(texto ?? '').trim();
  if (!s) return null;
  const partes = s.split(/[,;|\n]+|\s\/\s|\/(?=\s*[A-Za-z0-9ÁÉÍÓÚáéíóú])|\/\s*$/).map((p) => p.trim()).filter(Boolean);
  const grade: Record<string, number> = {};
  for (const parte of partes) {
    // O sufixo de até 4 letras é o que lê `2 ANOS 1` (a linha infantil); com
    // 2 letras o token era ilegível e a linha inteira caía em null.
    const m = parte.match(/^([A-Za-zÁÉÍÓÚáéíóú0-9]+(?:\s?[A-Za-z]{1,4})?)\s*[-:=\s]\s*(\d{1,5})$/);
    if (!m) {
      // Sem número: só passa se for um tamanho conhecido sozinho ("P", "XXGG").
      // ⚠️ NÚMERO SOLTO NÃO. `2`, `4`, `10` são tamanhos infantis válidos, então
      // um número perdido no texto passava por "rótulo vazio" e era descartado
      // calado — e com `total_pecas` zero a grade incompleta ainda reservava
      // menos peça do que o pedido pede. Rótulo vazio é sempre alfabético.
      if (!/^\d{1,2}$/.test(parte) && tamanhoCanonicoLisa(parte)) continue;
      return null;
    }
    const t = tamanhoCanonicoLisa(m[1]);
    if (!t) return null;
    grade[t] = (grade[t] ?? 0) + Number(m[2]);
  }
  return Object.keys(grade).length ? grade : null;
}

/**
 * Grade do item como o servidor a enxerga (`gradeDoItem`): o mapa quando
 * fecha com o total, senão o texto — ou null. É o que o bloco Materiais usa
 * para dizer "faltam N" POR TAMANHO em vez de somar a família.
 */
export function gradeDoItemLisa(item: {
  grade_json?: Record<string, number> | null;
  grade_texto?: string | null;
  total_pecas?: number | null;
}): Record<string, number> | null {
  const total = Number(item.total_pecas ?? 0) || 0;
  const fecha = (g: Record<string, number>) => {
    const soma = Object.values(g).reduce((a, b) => a + b, 0);
    return soma > 0 && (total <= 0 || soma === total);
  };
  const json = item.grade_json;
  if (json && typeof json === 'object' && !Array.isArray(json)) {
    const g: Record<string, number> = {};
    for (const [k, v] of Object.entries(json)) {
      const n = Number(v);
      if (!Number.isFinite(n) || n <= 0) continue;
      const t = tamanhoCanonicoLisa(k) ?? String(k).trim().toUpperCase();
      g[t] = (g[t] ?? 0) + Math.trunc(n);
    }
    if (fecha(g)) return g;
  }
  const doTexto = gradeDeTexto(item.grade_texto);
  return doTexto && fecha(doTexto) ? doTexto : null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Tolerância do recebimento — UM número para nota × balança
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Decisão da segunda simulação das histórias (ponta nº 11): qualquer diferença
 * acima de 1 grama entre a nota e a balança era divergência — exigia texto,
 * fechava o recebimento como DIVERGENTE e abria pendência para o comprador. Na
 * prática todo recebimento de malha fecharia divergente, e a pendência viraria
 * ruído. E havia um SEGUNDO número (1%) no mesmo arquivo para dizer se a compra
 * estava completa: dois números para o mesmo fato.
 *
 * Agora é um só, compartilhado pela tela (o campo de justificativa só aparece
 * fora dela) e pelo servidor (status do recebimento, pendência, fila de
 * chegadas). Peça se conta inteira: para `un` a tolerância é zero — 99 peças
 * numa nota de 100 é divergência, e o comprador precisa saber.
 */
export const TOLERANCIA_RECEBIMENTO = {
  /** 5% da quantidade da nota, para o que se pesa ou se mede (kg, m). */
  relativa: 0.05,
  /** Piso absoluto — o arredondamento de 3 casas. */
  absoluta: 0.0005,
} as const;

/** O texto que o servidor grava quando a diferença cabe na tolerância (o CHECK da 0083 exige justificativa para qualquer diferença). */
export const JUSTIFICATIVA_TOLERANCIA = 'Diferença de balança dentro da tolerância.';

export function toleranciaRecebimento(unidade: string | null | undefined, qtyNf: number): number {
  const u = String(unidade ?? 'un').toLowerCase();
  if (u === 'un') return TOLERANCIA_RECEBIMENTO.absoluta;
  return Math.max(TOLERANCIA_RECEBIMENTO.absoluta, Math.abs(qtyNf) * TOLERANCIA_RECEBIMENTO.relativa);
}

/** Nota × recebido fora da tolerância = divergência (justificativa, status DIVERGENTE, pendência). */
export function foraDaTolerancia(
  unidade: string | null | undefined,
  qtyNf: number | null | undefined,
  qtyReal: number | null | undefined,
): boolean {
  if (qtyNf === null || qtyNf === undefined || qtyReal === null || qtyReal === undefined) return false;
  return Math.abs(Number(qtyNf) - Number(qtyReal)) > toleranciaRecebimento(unidade, Number(qtyNf));
}

/**
 * Chave do GRUPO de malha no recebimento (§13.94): linhas do MESMO material, compradas para pedidos
 * diferentes (ou para o estoque, na compra avulsa) na mesma compra, são recebidas juntas — em rolo (os rolos são
 * pesados uma vez) ou a granel em kg/m (AGREGADO: um campo de quantidade só). Devolve null quando a linha não agrupa
 * (sem material, pacote, agregado em outra unidade, ou não é item de compra de pedido nem avulsa — retrabalho ou
 * linha sem demanda) e segue por linha.
 * Quem chama agrupa por esta chave e só trata como grupo quando 2+ linhas a compartilham.
 */
export function chaveDoGrupoDeMalha(l: {
  material_id: string | null | undefined;
  tracking: string | null | undefined;
  /** Unidade do material: só decide o AGREGADO (kg ou m agrupa; un e o resto não). */
  unidade?: string | null | undefined;
  gc_compra_id: string | null | undefined;
  /** Item de compra de PEDIDO ou AVULSA (2026-10-29: o rolo que atende o pedido e a compra para estoque), sem retrabalho. */
  de_compra: boolean;
}): string | null {
  if (!l.material_id || l.de_compra !== true) return null;
  const unidade = String(l.unidade ?? '').toLowerCase();
  const agregadoAgrupavel = l.tracking === 'AGREGADO' && (unidade === 'kg' || unidade === 'm');
  if (l.tracking !== 'INDIVIDUAL' && !agregadoAgrupavel) return null;
  return `${l.material_id}|${l.gc_compra_id ?? ''}`;
}

/**
 * As linhas que ficam no grupo (§13.94, 2026-10-29). A compra AVULSA (sem pedido) só divide o rolo com o pedido quando
 * TODA linha tem quantidade na nota: sem ela não há como saber quanto é do pedido e quanto é do estoque, e a avulsa
 * levaria o rolo antes do pedido. Nesse caso a avulsa volta a ser um cartão próprio, como antes. Quem chama descarta o
 * grupo que ficar com menos de 2 linhas — a mesma regra na tela e no `/complete`.
 */
export function linhasDoGrupoDeMalha<T extends { qty_nf: number | null | undefined; tem_pedido: boolean }>(linhas: T[]): T[] {
  const semNota = linhas.some((l) => l.qty_nf === null || l.qty_nf === undefined);
  return semNota ? linhas.filter((l) => l.tem_pedido) : linhas;
}

const arred3 = (n: number): number => Math.round((n + Number.EPSILON) * 1000) / 1000;

/**
 * Reparte o total pesado do grupo entre as linhas, já ordenadas por prazo (a mais urgente primeiro):
 * cada uma recebe até o `qty_nf`, e o que sobrar vai para a ÚLTIMA (a menos urgente). A última fecha a
 * conta (total − soma das anteriores), então a soma das partes é sempre exatamente o total arredondado.
 */
export function dividirChegadaPeloPrazo(
  total: number,
  linhas: Array<{ id: string; qty_nf: number | null }>,
): Map<string, number> {
  const partes = new Map<string, number>();
  if (linhas.length === 0) return partes;
  const alvo = Number.isFinite(total) && total > 0 ? arred3(total) : 0;
  let restante = alvo;
  let somado = 0;
  linhas.forEach((linha, i) => {
    let parte: number;
    if (i === linhas.length - 1) {
      parte = Math.max(0, arred3(alvo - somado));
    } else {
      parte = arred3(Math.min(restante, Math.max(0, Number(linha.qty_nf ?? 0))));
    }
    partes.set(linha.id, parte);
    somado = arred3(somado + parte);
    restante = Math.max(0, arred3(alvo - somado));
  });
  return partes;
}

/**
 * Reparte o total pesado do grupo na PROPORÇÃO do `qty_nf` de cada linha (§13.94) — para a diferença de balança, dentro
 * da tolerância: toda linha desvia o mesmo % do grupo (≤ tolerância) e fecha pela regra normal, sem mecanismo à parte.
 * A última fecha a conta, então a soma é exatamente o total arredondado. Σ qty_nf ≤ 0 não tem proporção: quem chama
 * cai em `dividirChegadaPeloPrazo`.
 */
export function dividirChegadaProporcional(
  total: number,
  linhas: Array<{ id: string; qty_nf: number | null }>,
): Map<string, number> {
  const partes = new Map<string, number>();
  if (linhas.length === 0) return partes;
  const alvo = Number.isFinite(total) && total > 0 ? arred3(total) : 0;
  const somaNf = linhas.reduce((a, l) => a + Math.max(0, Number(l.qty_nf ?? 0)), 0);
  let somado = 0;
  linhas.forEach((linha, i) => {
    const parte = i === linhas.length - 1
      ? Math.max(0, arred3(alvo - somado))
      : (somaNf > 0 ? arred3(Math.max(0, Number(linha.qty_nf ?? 0)) * alvo / somaNf) : 0);
    partes.set(linha.id, parte);
    somado = arred3(somado + parte);
  });
  return partes;
}

// ─────────────────────────────────────────────────────────────────────────────
// Exceções registradas (§13.74) — ESPELHO de `server/src/lib/stock-rules.ts`
// ─────────────────────────────────────────────────────────────────────────────
// A operação nunca trava: a exceção é permitida, avisada e fica gravada no
// lançamento, com motivo. `tests/stock-rules.test.ts` confere a paridade.

export const EXCECAO_TIPOS = [
  'TROCA_COMBINADA', 'GRADE_DIVERGENTE', 'FAMILIA_DIVERGENTE', 'RESERVA_DE_OUTRO_PEDIDO', 'RETIRADA_FORA_DO_FLUXO',
  'REPOSICAO_DE_PECA', 'SAIDA_APOS_BAIXA_AUTOMATICA',
] as const;
export type ExcecaoTipo = typeof EXCECAO_TIPOS[number];

/** Por que a peça saiu para o pedido DEPOIS de a separação fechar (§13.83). Espelho do servidor. */
export const MOTIVOS_REPOSICAO = ['DEFEITO', 'REDUCAO_TAMANHO', 'OUTRO'] as const;
export type MotivoReposicao = typeof MOTIVOS_REPOSICAO[number];

export const LABEL_MOTIVO_REPOSICAO: Record<MotivoReposicao, string> = {
  DEFEITO: 'Peça com defeito',
  REDUCAO_TAMANHO: 'Reduzi de um tamanho maior',
  OUTRO: 'Outro motivo',
};

export const MOTIVOS_FALTA = ['NAO_TEM', 'DEFEITO', 'COR_ERRADA', 'OUTRO'] as const;
export type MotivoFalta = typeof MOTIVOS_FALTA[number];

export const MOTIVOS_CONFIRMACAO = ['CLIENTE_COMBINOU', 'DEFEITO_NA_PECA', 'ERRO_NO_PEDIDO', 'OUTRO'] as const;
export type MotivoConfirmacao = typeof MOTIVOS_CONFIRMACAO[number];

export const MOTIVOS_RETIRADA = ['REPOSICAO', 'RETRABALHO', 'AMOSTRA', 'OUTRO'] as const;
export type MotivoRetirada = typeof MOTIVOS_RETIRADA[number];

export const MOTIVOS_SEM_ENTRADA = ['JA_CONTADA_NO_INVENTARIO', 'OUTRO'] as const;
export type MotivoSemEntrada = typeof MOTIVOS_SEM_ENTRADA[number];

/** O que aconteceu com a amostra (§13.84). Espelho do servidor. */
export const DESFECHOS_AMOSTRA = ['DEVOLVIDA', 'CORTESIA', 'VENDA'] as const;
export type DesfechoAmostra = typeof DESFECHOS_AMOSTRA[number];
export const LABEL_DESFECHO_AMOSTRA: Record<DesfechoAmostra, string> = {
  DEVOLVIDA: 'Devolvida',
  CORTESIA: 'Ficou com o cliente (cortesia)',
  VENDA: 'Virou venda',
};

export const LABEL_MOTIVO_SEM_ENTRADA: Record<MotivoSemEntrada, string> = {
  JA_CONTADA_NO_INVENTARIO: 'As peças já estão no estoque (contadas no inventário)',
  OUTRO: 'Outro motivo',
};

export const NAO_INFORMADO = 'NAO_INFORMADO';

/** Espelho de `JANELA_REGULARIZACAO_HORAS` do servidor (§13.57/§13.74). */
export const JANELA_REGULARIZACAO_HORAS = 48;

/** Etapa CONCLUÍDA há mais que a janela; `data_fim` ilegível = dentro dela. */
export function etapaEncerradaHaMuito(status: unknown, dataFim: unknown, agora: number = Date.now()): boolean {
  if (status !== 'CONCLUIDO') return false;
  const t = Date.parse(String(dataFim ?? ''));
  return Number.isFinite(t) && agora - t > JANELA_REGULARIZACAO_HORAS * 3_600_000;
}

export const LABEL_MOTIVO_FALTA: Record<MotivoFalta, string> = {
  NAO_TEM: 'Não tem',
  DEFEITO: 'Defeito',
  COR_ERRADA: 'Cor errada',
  OUTRO: 'Outro motivo',
};

export const LABEL_MOTIVO_CONFIRMACAO: Record<MotivoConfirmacao, string> = {
  CLIENTE_COMBINOU: 'O cliente combinou assim',
  DEFEITO_NA_PECA: 'Defeito na peça',
  ERRO_NO_PEDIDO: 'Erro no pedido',
  OUTRO: 'Outro motivo',
};

export const LABEL_MOTIVO_RETIRADA: Record<MotivoRetirada, string> = {
  REPOSICAO: 'Reposição',
  RETRABALHO: 'Retrabalho',
  AMOSTRA: 'Amostra',
  OUTRO: 'Outro motivo',
};

export const LABEL_EXCECAO: Record<ExcecaoTipo, string> = {
  TROCA_COMBINADA: 'troca combinada com o cliente',
  GRADE_DIVERGENTE: 'quantidade diferente do pedido',
  FAMILIA_DIVERGENTE: 'peça diferente da vendida',
  RESERVA_DE_OUTRO_PEDIDO: 'material reservado para outro pedido',
  RETIRADA_FORA_DO_FLUXO: 'retirada fora do fluxo',
  REPOSICAO_DE_PECA: 'reposição depois da separação',
  SAIDA_APOS_BAIXA_AUTOMATICA: 'saída depois da baixa automática da costura',
};

/** Metadata `excecao` de um lançamento, como o servidor grava. */
export interface ExcecaoRegistrada {
  tipo: ExcecaoTipo;
  motivo: string;
  nota?: string;
  por: string | null;
  em: string;
}

/** "troca combinada com o cliente — Defeito (nota)" — para a lista de movimentos e as Exceções do dia. */
export function rotuloExcecao(e: Partial<ExcecaoRegistrada> | null | undefined): string {
  if (!e?.tipo) return '';
  const base = LABEL_EXCECAO[e.tipo as ExcecaoTipo] ?? String(e.tipo).toLowerCase().replace(/_/g, ' ');
  const motivo = e.motivo && e.motivo !== NAO_INFORMADO
    ? (LABEL_MOTIVO_FALTA as Record<string, string>)[e.motivo]
      ?? (LABEL_MOTIVO_CONFIRMACAO as Record<string, string>)[e.motivo]
      ?? (LABEL_MOTIVO_RETIRADA as Record<string, string>)[e.motivo]
      ?? (LABEL_MOTIVO_REPOSICAO as Record<string, string>)[e.motivo]
      ?? (e.motivo === 'CONFIRMADO' ? 'confirmado pelo operador' : e.motivo)
    : 'motivo não informado';
  return `${base} — ${motivo}${e.nota ? ` (${e.nota})` : ''}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Devolução de rolos ao fornecedor (0153, REGRAS §13.106)
// ─────────────────────────────────────────────────────────────────────────────

/** As reservas ATIVAS que o saldo deixou de cobrir depois do bipe — só aviso (decisão do dono). */
export interface AvisoReservaDescoberta {
  material_id: string;
  material: string;
  unidade: string;
  saldo: number;
  reservado: number;
  falta: number;
  pedidos: Array<{ numero_pedido: string | null; restante: number }>;
}

/** Um rolo da devolução é o próprio lançamento (AJUSTE a zero): o "Desfazer" é o estorno dele. */
export type RoloDevolvido = Movimento & { valor_unitario: string | number | null };

export interface DevolucaoFornecedor {
  id: string;
  gc_fornecedor_id: string | null;
  fornecedor_nome: string | null;
  criado_por_nome: string | null;
  created_at: string;
  /** A NF vem SEMPRE do espelho das NF-e do GC: cancelada lá depois, aparece cancelada aqui. */
  nf_gc_id: string | null;
  nf_loja_id: string | null;
  nf_loja_nome: string | null;
  nf_numero: string | null;
  nf_situacao: string | null;
  nf_valor: number | null;
  nf_emissao: string | null;
  nf_cfop: string | null;
  nf_natureza: string | null;
  nf_ligada_por: string | null;
  nf_ligada_em: string | null;
  rolos: RoloDevolvido[];
  materiais: Array<{ material_id: string; material: string; unidade: string; rolos: number; qtd: number; valor: number; sem_preco: number }>;
  totais: { rolos: number; kg: number; m: number; valor: number; sem_preco: number };
}
